import { useCallback, useEffect, useState } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { useSharedStateSelector } from '@trello/shared-state';

import { workspaceBoardsFilterState } from './workspaceBoardsFilterState';
import { useWorkspaceBoardsPagedQuery } from './WorkspaceBoardsPagedQuery.generated';

type TraceCache = Record<
  string,
  {
    traceId: string;
    traceComplete?: boolean;
  }
>;

const getCacheKey = (
  searchQuery: string | null | undefined,
  sortBy: string | null | undefined,
  sortOrder: string | null | undefined,
  tagFilter: string | null | undefined,
): string =>
  [searchQuery, sortBy, sortOrder, tagFilter].filter(Boolean).join('-');

/**
 * Hook responsible for requesting the boards that match the current
 * search, filter and sorting params
 */
export const useWorkspaceBoardsFilteredQuery = ({
  orgId,
}: {
  orgId: string;
}) => {
  const { limit, searchQuery, sortBy, sortOrder, tagFilter } =
    useSharedStateSelector(
      workspaceBoardsFilterState,
      useCallback(
        (state) => ({
          limit: state.limit,
          searchQuery: state.searchQuery,
          tagFilter: state.tagFilter,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
        [],
      ),
    );

  const [traceCache, setTraceCache] = useState<TraceCache>({});
  const [allBoardsFetched, setAllBoardsFetched] = useState(false);

  /**
   * Whenever the search params change, trigger a task start event
   * if we haven't already started one for this set of params
   */
  useEffect(() => {
    const cacheKey = getCacheKey(searchQuery, sortBy, sortOrder, tagFilter);
    if (!traceCache[cacheKey]?.traceId) {
      setTraceCache((cache) => ({
        ...cache,
        [cacheKey]: {
          traceId: Analytics.startTask({
            taskName: 'view-organization/boards',
            source: 'workspaceBoardsScreen',
            containers: formatContainers({ idOrganization: orgId }),
            attributes: { sortBy, sortOrder, tagFilter, search: !!searchQuery },
          }),
          traceComplete: false,
        },
      }));
    }
  }, [
    orgId,
    searchQuery,
    sortBy,
    sortOrder,
    tagFilter,
    traceCache,
    setTraceCache,
  ]);

  /**
   * Fetch the first page of boards based on the current search & filter params
   * If we get back fewer results than we asked for, set the allBoardsFetched state
   * to true
   */
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, fetchMore, refetch } = useWorkspaceBoardsPagedQuery({
    variables: {
      id: orgId,
      limit,
      search: searchQuery,
      tags: tagFilter ? [tagFilter] : undefined,
      filter: 'open',
      sortOrder,
      sortBy,
    },
    onCompleted: (response) => {
      const cacheKey = getCacheKey(searchQuery, sortBy, sortOrder, tagFilter);

      setAllBoardsFetched(response?.organizationBoards.length < limit);
      setTraceCache((cache) => {
        const task = cache[cacheKey];
        if (task?.traceId && !task?.traceComplete) {
          Analytics.taskSucceeded({
            taskName: 'view-organization/boards',
            source: 'workspaceBoardsScreen',
            containers: formatContainers({ idOrganization: orgId }),
            attributes: { sortBy, sortOrder, tagFilter, search: !!searchQuery },
            traceId: task.traceId,
          });
          task.traceComplete = true;
        }

        return { ...cache, [cacheKey]: { ...task } };
      });
    },
    onError: (err) => {
      const cacheKey = getCacheKey(searchQuery, sortBy, sortOrder, tagFilter);

      setAllBoardsFetched(false);
      setTraceCache((cache) => {
        const task = cache[cacheKey];
        if (task?.traceId && !task?.traceComplete) {
          Analytics.taskFailed({
            taskName: 'view-organization/boards',
            source: 'workspaceBoardsScreen',
            containers: formatContainers({ idOrganization: orgId }),
            attributes: { sortBy, sortOrder, tagFilter, search: !!searchQuery },
            traceId: task.traceId,
            error: err,
          });

          sendErrorEvent(err, {
            tags: {
              ownershipArea: 'trello-billing-platform',
              feature: 'Workspace Boards Page',
            },
          });

          task.traceComplete = true;
        }

        return { ...cache, [cacheKey]: { ...task } };
      });
    },
    waitOn: ['MemberBoards'],

    /**
     * This query can't really be cached due to all the different search
     * and filter params that can be applied and all the different avenues
     * in the app to create or close boards that would invalidate the cache
     */
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  /**
   * Fetch the next page of boards based on the current search & filter params
   * If we get back fewer results than we asked for, then this was the last
   * page, so set the allBoardsFetched state to true
   */
  const fetchNextPage = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'showMoreButton',
      source: 'workspaceBoardsScreen',
      containers: formatContainers({ idOrganization: orgId }),
    });

    // the startIndex param in the API is 1-based
    const offset = (data?.organizationBoards.length ?? 0) + 1;
    const cacheKey = getCacheKey(searchQuery, sortBy, sortOrder, tagFilter);
    setTraceCache((cache) => ({
      ...cache,
      [cacheKey]: {
        traceId: Analytics.startTask({
          taskName: 'view-organization/boards/more',
          source: 'workspaceBoardsScreen',
          containers: formatContainers({ idOrganization: orgId }),
          attributes: {
            sortBy,
            sortOrder,
            tagFilter,
            search: !!searchQuery,
            offset,
          },
        }),
        traceComplete: false,
      },
    }));

    try {
      const nextPage = await fetchMore({
        variables: { offset },
      });

      setAllBoardsFetched(
        Boolean(nextPage.data?.organizationBoards.length < limit),
      );
      setTraceCache((cache) => {
        const task = cache[cacheKey];
        if (task?.traceId && !task?.traceComplete) {
          Analytics.taskSucceeded({
            taskName: 'view-organization/boards/more',
            source: 'workspaceBoardsScreen',
            containers: formatContainers({ idOrganization: orgId }),
            attributes: {
              sortBy,
              sortOrder,
              tagFilter,
              search: !!searchQuery,
              offset,
            },
            traceId: task.traceId,
          });
          task.traceComplete = true;
        }

        return { ...cache, [cacheKey]: { ...task } };
      });
    } catch (err) {
      setAllBoardsFetched(false);
      setTraceCache((cache) => {
        const task = cache[cacheKey];
        if (task?.traceId && !task?.traceComplete) {
          Analytics.taskFailed({
            taskName: 'view-organization/boards/more',
            source: 'workspaceBoardsScreen',
            containers: formatContainers({ idOrganization: orgId }),
            attributes: {
              sortBy,
              sortOrder,
              tagFilter,
              search: !!searchQuery,
              offset,
            },
            traceId: task.traceId,
            error: err,
          });

          sendErrorEvent(err, {
            tags: {
              ownershipArea: 'trello-billing-platform',
              feature: 'Workspace Boards Page',
            },
          });

          task.traceComplete = true;
        }

        return { ...cache, [cacheKey]: { ...task } };
      });
    }
  }, [
    fetchMore,
    orgId,
    setTraceCache,
    data?.organizationBoards.length,
    limit,
    searchQuery,
    sortBy,
    sortOrder,
    tagFilter,
  ]);

  /**
   * Refresh the current query based on the current search & filter params,
   * including all pages of results that have been fetched so far. Use this
   * to re-sync the entire current view when a board has been reopened so it
   * will appear in the list. Note we're adding 1 to the limit to ensure we
   * have room to add that reopened board to the list.
   *
   * This workaround is needed because we don't know where the reopened board
   * would appear due to all the sorting and filtering options you may have
   * active, so we just refresh the whole list.
   */
  const refetchCurrentQuery = useCallback(async () => {
    refetch({
      id: orgId,
      limit: Math.max((data?.organizationBoards.length ?? 0) + 1, limit),
      search: searchQuery,
      tags: tagFilter ? [tagFilter] : undefined,
      filter: 'open',
      sortOrder,
      sortBy,
    });
  }, [
    refetch,
    orgId,
    limit,
    searchQuery,
    tagFilter,
    sortBy,
    sortOrder,
    data?.organizationBoards.length,
  ]);

  return {
    isLoading: loading,
    allBoardsFetched,
    fetchNextPage,
    refetchCurrentQuery,
    boards: data?.organizationBoards ?? [],
  };
};
